import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HomePage from './pages/HomePage.jsx';
import ProfessionalHomePage from './pages/ProfessionalHomePage.jsx';
import SearchResults from './pages/SearchResults.jsx';
import QuoteRequestForm from './pages/QuoteRequestForm.jsx';
import UserDashboard from './pages/UserDashboard.jsx';
import CustomerBookingDetail from './pages/CustomerBookingDetail.jsx';
import CustomerPortalLayout from './pages/CustomerPortalLayout.jsx';
import CustomerPortalSection from './pages/CustomerPortalSection.jsx';
import CustomerBookingsPage from './pages/CustomerBookingsPage.jsx';
import MoverDashboard from './pages/MoverDashboard.jsx';
import AuthPage from './pages/AuthPage.jsx';
import VerifyEmailPage from './pages/VerifyEmailPage.jsx';
import AccountSetupPage from './pages/AccountSetupPage.jsx';
import LiveChatWidget from './components/LiveChatWidget.jsx';
import Navbar from './components/Navbar';
import Footer from './components/Footer.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import Hero from './components/Hero';
import { logPageView } from './utils/analytics.js';
import HowItWorks from './pages/HowItWorks.jsx';
import MovingCompanies from './pages/MovingCompanies.jsx';
import Results from './pages/Results.jsx';
import Dashboard from './pages/Dashboard.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import FAQ from './pages/FAQ.jsx';
import Blog from './pages/Blog.jsx';
import Services from './pages/Services.jsx';
import Portfolio from './pages/Portfolio.jsx';
import Testimonials from './pages/Testimonials.jsx';
import Team from './pages/Team.jsx';
import Pricing from './pages/Pricing.jsx';

const liveChatEnabled = import.meta.env.VITE_ENABLE_LIVE_CHAT === 'true';

// Conditional LiveChat component that doesn't show in mover portal, admin portal, or admin dashboard
const ConditionalLiveChat = () => {
  const location = useLocation();
  const [quoteActive, setQuoteActive] = useState(document.body.classList.contains('quote-flow-active'));
  useEffect(() => {
    const observer = new MutationObserver(() => setQuoteActive(document.body.classList.contains('quote-flow-active')));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  
  // Don't show LiveChatWidget until a deployment explicitly enables a support-backed chat experience.
    if (!liveChatEnabled ||
        quoteActive ||
        location.pathname.startsWith('/customer-portal') ||
        location.pathname.startsWith('/mover-portal') ||
        location.pathname.startsWith('/admin-portal') ||
        location.pathname === '/admin-dashboard') {
    return null;
  }
  
  return <LiveChatWidget />;
};
import MoverPortalWrapper from './pages/MoverPortalWrapper.jsx';
import AdminPortal from './pages/AdminPortal.jsx';
import AdminMoverApproval from './components/AdminMoverApproval.jsx';
import Booking from './pages/Booking.jsx';
import NotFound from './pages/NotFound.jsx';
import Legal from './pages/Legal.jsx';
import QuoteDetails from './pages/QuoteDetails.jsx';
import { ErrorBoundary } from './components/ErrorBoundary.jsx';
import BecomePartner from './pages/BecomePartner.jsx';
import JobCompletionApproval from './components/JobCompletionApproval.jsx';
import { Navigate } from 'react-router-dom';
import { MapsProvider } from './components/MapsProvider.jsx';
import { getApiBase } from './utils/apiConfig';

// Dynamically import heavy or below-the-fold components
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard.jsx'));
const BookingChat = lazy(() => import('./components/BookingChat.jsx'));
const TestimonialCarousel = lazy(() => import('./components/TestimonialCarousel.jsx'));

const ConditionalFooter = () => {
  const location = useLocation();
  const [quoteActive, setQuoteActive] = useState(document.body.classList.contains('quote-flow-active'));
  useEffect(() => {
    const observer = new MutationObserver(() => setQuoteActive(document.body.classList.contains('quote-flow-active')));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  if (
    quoteActive ||
    location.pathname.startsWith('/customer-portal') ||
    location.pathname.startsWith('/mover-portal') ||
    location.pathname.startsWith('/admin-portal') ||
    location.pathname === '/admin-dashboard'
  ) return null;
  return <Footer />;
};

const StripeReturnRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stripeResult = new URLSearchParams(location.search).get('stripe');
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(stripeResult === 'complete');

  useEffect(() => {
    if (stripeResult !== 'complete') return undefined;

    const token = localStorage.getItem('token');
    if (!token) {
      setChecking(false);
      return undefined;
    }

    fetch(`${getApiBase()}/stripe-connect/account-status/me`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(response => response.json())
      .then(setStatus)
      .catch(() => setStatus({ error: true }))
      .finally(() => setChecking(false));
  }, [stripeResult]);

  if (stripeResult !== 'complete' && stripeResult !== 'refresh') return null;

  const complete = stripeResult === 'complete' && status?.isOnboardingComplete;
  const title = stripeResult === 'refresh'
    ? 'Payout setup was not completed'
    : complete ? 'Payout account connected' : 'Payout setup submitted';
  const message = stripeResult === 'refresh'
    ? 'Stripe sent you back before all payout details were finished. You can continue from your payout settings.'
    : complete
      ? 'Your Stripe payout account is active. MOVD can send payouts to this account.'
      : 'Stripe received your information. We are checking the account status now. If anything is still required, you can finish it from your payout settings.';

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center',
      justifyContent: 'center', bgcolor: '#F5F7FA', p: 3,
    }}>
      <Paper elevation={3} sx={{ width: '100%', maxWidth: 560, p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          {checking ? <CircularProgress size={48} /> : complete ? <CheckCircleIcon color="success" sx={{ fontSize: 56 }} /> : <WarningAmberIcon color="warning" sx={{ fontSize: 56 }} />}
          <Typography variant="h5" fontWeight={800}>{checking ? 'Checking your payout account...' : title}</Typography>
          {!checking && <Typography color="text.secondary">{message}</Typography>}
          {!checking && status?.error && <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>We could not verify the status automatically. Your details may still be processing in Stripe.</Alert>}
          {!checking && (
            <Button variant="contained" size="large" onClick={() => navigate('/mover-portal/financials', { replace: true })}>
              Return to payout settings
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

const RouteIntentRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const portalIntent = new URLSearchParams(location.search).get('portal');

  useEffect(() => {
    if (portalIntent === 'mover-login') {
      navigate('/mover-portal/login', { replace: true });
    }
  }, [navigate, portalIntent]);

  return null;
};

// Component to conditionally render navbar based on route
const ConditionalNavbar = () => {
  const location = useLocation();
  const [quoteActive, setQuoteActive] = useState(document.body.classList.contains('quote-flow-active'));
  useEffect(() => {
    const observer = new MutationObserver(() => setQuoteActive(document.body.classList.contains('quote-flow-active')));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const isMoverPortal = location.pathname.startsWith('/mover-portal');
  const isCustomerPortal = location.pathname.startsWith('/customer-portal');
  const isAdminPortal = location.pathname.startsWith('/admin-portal');
  const isAdminDashboard = location.pathname === '/admin-dashboard';
  
  // Don't render navbar for mover portal, admin portal, or admin dashboard routes
  if (quoteActive || isMoverPortal || isCustomerPortal || isAdminPortal || isAdminDashboard) {
    return null;
  }
  
  return <Navbar />;
};

const ConditionalNavbarSpacer = () => {
  const location = useLocation();
  const [quoteActive, setQuoteActive] = useState(document.body.classList.contains('quote-flow-active'));
  useEffect(() => {
    const observer = new MutationObserver(() => setQuoteActive(document.body.classList.contains('quote-flow-active')));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  if (quoteActive || location.pathname.startsWith('/customer-portal') || location.pathname.startsWith('/mover-portal') || location.pathname.startsWith('/admin-portal')) return null;
  return <Box sx={{ height: { xs: 64, md: 72 } }} />;
};

function App() {
  useEffect(() => {
    logPageView(window.location.pathname);
  }, []);

  return (
    /* Single global Google Maps loader */
    <MapsProvider>
        <Router>
          <ScrollToTop />
          <StripeReturnRedirect />
          <RouteIntentRedirect />
          <ConditionalNavbar />
          {/* Spacer so content doesn't sit under the fixed navbar */}
          <ConditionalNavbarSpacer />
          {/* <Hero /> Removed to prevent blank/duplicate hero box */}
          {/* Removed .container for true full-width layout */}
          <ErrorBoundary>
            <Suspense fallback={
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '50vh',
                flexDirection: 'column',
                gap: 2
              }}>
                <Typography variant="h6">Loading...</Typography>
                <Box sx={{ width: 200, height: 4, bgcolor: 'grey.200', borderRadius: 2 }}>
                  <Box sx={{ 
                    width: '60%', 
                    height: '100%', 
                    bgcolor: 'primary.main', 
                    borderRadius: 2,
                    animation: 'loading 1.5s ease-in-out infinite'
                  }} />
                </Box>
              </Box>
            }>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/professional" element={<ProfessionalHomePage />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/moving-companies" element={<MovingCompanies />} />
                {/* <Route path="/get-quote" element={<GetQuote />} /> */}
                <Route path="/get-quote" element={<Navigate to="/" replace />} />
                <Route path="/results" element={<Navigate to="/" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/services" element={<Services />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/team" element={<Team />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/quote/:moverId" element={<QuoteRequestForm />} />
                <Route path="/inventory" element={<Navigate to="/" replace />} />
                <Route path="/booking" element={<Navigate to="/" replace />} />
                <Route path="/move-complete/:paymentId" element={<JobCompletionApproval />} />
                <Route path="/user-dashboard" element={<Navigate to="/customer-portal/bookings" replace />} />
                <Route path="/user-dashboard/:bookingId" element={<CustomerBookingDetail />} />
                <Route path="/customer-portal" element={<CustomerPortalLayout />}>
                  <Route index element={<Navigate to="bookings" replace />} />
                  <Route path="bookings" element={<CustomerBookingsPage />} />
                  <Route path="booking/:bookingId" element={<CustomerBookingDetail />} />
                  <Route path=":section" element={<CustomerPortalSection />} />
                </Route>
                <Route path="/mover-dashboard" element={<Navigate to="/mover-portal/dashboard" replace />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/account-setup" element={<AccountSetupPage />} />
                <Route path="/mover-signup" element={<Navigate to="/become-partner" replace />} />
                <Route path="/mover-portal/*" element={<MoverPortalWrapper />} />
                <Route path="/admin-portal/*" element={<AdminPortal />} />
                <Route path="/admin-dashboard" element={<Navigate to="/admin-portal" replace />} />
                <Route path="/admin/movers" element={<AdminMoverApproval />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/quote-details" element={<Navigate to="/" replace />} />
                <Route path="/become-partner" element={<BecomePartner />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              {/* Example usage of dynamically loaded components */}
              {/* <AnalyticsDashboard /> */}
              {/* <BookingChat /> */}
              {/* <TestimonialCarousel /> */}
            </Suspense>
          </ErrorBoundary>
          <ConditionalFooter />
          <ConditionalLiveChat />
        </Router>
    </MapsProvider>
  );
}

export default App;
